/**
 * The "there's a chart below" cue.
 *
 * The stacked layout hangs the chart a full screen under the grid, and off-screen content
 * nobody asked for is content nobody finds. This is the only hint it's there, so it stays
 * small, clear of the grid's controls, and one click away.
 *
 * Always mounted, hidden with CSS. Staying in place and toggling opacity keeps it out of the
 * page flow, where a mounted-and-unmounted floating strip is easy to get wrong.
 */

import { useMemo, type CSSProperties } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface ChartBelowFoldIndicatorProps {
    /** Whether the chart is out of view and the cue applies. */
    open: boolean;
    /** Bring the chart into view. */
    onClick: () => void;
}

const FADE_MS = 200;

/** How far the pill sits above the bottom edge of the grid. */
const LIFT_PX = 24;

/**
 * A zero-height marker in the flow, and the cue's anchor.
 *
 * Rendered between the grid and the chart, so its position is the grid's bottom edge, which
 * is where the pill hangs from. It brings its own `relative` rather than asking an ancestor,
 * since those containers are shared with the rest of the workspace.
 *
 * Neither `fixed` nor `sticky` fits: `fixed` is viewport-relative only while no ancestor
 * establishes a containing block, so it changes meaning as the tree changes; `sticky` is
 * clamped to a block that here is the very area the chart overflows.
 */
const markerStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
    height: 0,
};

/**
 * The pill's box. Inline rather than `sx` on purpose: a generated class can lose to
 * injection order or to the legacy `@mui/styles` sheet this app still carries, and losing
 * any of these is a layout bug — without `absolute` the strip pushes the chart down the
 * page, and without the hiding the button covers what the user is reading.
 *
 * Centred by a full-width strip rather than a translate, so the pill's size is never needed.
 */
function usePillStyle(open: boolean): CSSProperties {
    const theme = useTheme();
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    return useMemo(
        () => ({
            position: 'absolute',
            bottom: LIFT_PX,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: theme.zIndex.fab,
            // The strip spans the grid; only the pill itself should catch the pointer.
            pointerEvents: 'none',
            opacity: open ? 1 : 0,
            // Hidden, not just transparent: a see-through button is still a tab stop and
            // still reachable by a screen reader. Held until the fade finishes.
            visibility: open ? 'visible' : 'hidden',
            transition: reduceMotion
                ? undefined
                : open
                  ? `opacity ${FADE_MS}ms ease`
                  : `opacity ${FADE_MS}ms ease, visibility 0s ${FADE_MS}ms`,
        }),
        [open, reduceMotion, theme.zIndex.fab]
    );
}

const appHeaderBlue = '#1976d2';

const buttonSx = {
    pointerEvents: 'auto',
    borderRadius: 5,
    textTransform: 'none',
    boxShadow: 3,
    backgroundColor: appHeaderBlue,
    borderColor: 'gray',
    transition: 'transform 0.15s ease-in-out',
    color: 'background.paper',
    '&:hover': { transform: 'translateY(-2px)' },
};

export default function ChartBelowFoldIndicator({ open, onClick }: ChartBelowFoldIndicatorProps) {
    const pillStyle = usePillStyle(open);

    return (
        <Box style={markerStyle}>
            {/* `visibility` takes the button out of the tab order; `aria-hidden` takes it
                out of the accessibility tree too, so the cue reads as absent rather than
                as a silent control. */}
            <Box style={pillStyle} aria-hidden={!open}>
                <Button
                    size='small'
                    variant='outlined'
                    color='inherit'
                    onClick={onClick}
                    startIcon={<KeyboardArrowDownIcon fontSize='small' />}
                    sx={buttonSx}
                >
                    Chart below
                </Button>
            </Box>
        </Box>
    );
}
